import { postInvoicedVendorCredits as postInv, postPaidCredits as postPaid, postPartialCredits as postPart, postCancellationReversals as postRev } from './contractsLedgerPosting';

// Simple idempotency helper: compute a stable token for a posting intent
const makeToken = ({ contractId, milestoneId, eventType, amountFingerprint }) => {
  const raw = [contractId || '', milestoneId || '', eventType || '', JSON.stringify(amountFingerprint || {})].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return `tok_${eventType}_${Math.abs(hash)}`;
};

export const buildPostingRef = ({ contractId, milestoneId, eventType, amountFingerprint }) => ({
  eventType,
  token: makeToken({ contractId, milestoneId, eventType, amountFingerprint }),
  createdAt: new Date().toISOString(),
});

export const hasPostingToken = (milestone, token) => {
  const refs = Array.isArray(milestone?.postingRefs) ? milestone.postingRefs : [];
  return refs.some((r) => r && r.token === token);
};

export const postInvoicedVendorCreditsIdem = async ({ contractId, milestone, vendorComponents, createdBy }) => {
  const tokenRef = buildPostingRef({ contractId, milestoneId: milestone?.id, eventType: 'Invoiced', amountFingerprint: vendorComponents });
  if (hasPostingToken(milestone, tokenRef.token)) return { skipped: true, token: tokenRef.token };
  const res = await postInv({ contractId, paymentLine: { id: milestone.id, milestoneId: milestone.id, vendorComponents }, createdBy });
  return { ok: true, tokenRef, result: res };
};

export const postPaidCreditsIdem = async ({ contractId, milestone, lineAmountUsd, partnerCommissionUsd, processingUsd, serviceMargins, transportUsd, partnerRoles, beneficiaryCompanyId, createdBy }) => {
  const fp = {
    lineAmountUsd,
    partnerCommissionUsd,
    processingUsd,
    serviceMargins,
    transportUsd,
    partnerRoles: (partnerRoles || []).map(r => ({ entityId: r.entityId, percent: r.percent })),
  };
  const tokenRef = buildPostingRef({ contractId, milestoneId: milestone?.id, eventType: 'Paid', amountFingerprint: fp });
  
  // Debug logging
  console.log('[postPaidCreditsIdem] Called with:', {
    contractId,
    milestoneId: milestone?.id,
    lineAmountUsd,
    partnerCommissionUsd,
    processingUsd,
    serviceMargins,
    transportUsd,
    partnerRolesCount: (partnerRoles || []).length,
    partnerRoles: partnerRoles,
    beneficiaryCompanyId,
    tokenRef: tokenRef.token,
    hasExistingToken: hasPostingToken(milestone, tokenRef.token),
    milestonePostingRefs: milestone?.postingRefs,
  });
  
  if (hasPostingToken(milestone, tokenRef.token)) {
    console.log('[postPaidCreditsIdem] Skipping - token already exists');
    return { skipped: true, token: tokenRef.token };
  }
  
  console.log('[postPaidCreditsIdem] Proceeding with posting...');
  const res = await postPaid({ contractId, paymentLine: { id: milestone.id, lineAmountUsd }, partnerCommissionUsd, processingUsd, serviceMargins, transportUsd, partnerRoles, beneficiaryCompanyId, createdBy });
  console.log('[postPaidCreditsIdem] Posting result:', res);
  return { ok: true, tokenRef, result: res };
};

export const postPartialCreditsIdem = async ({ contractId, milestone, appliedUsd, totalUsd, partnerRoles, beneficiaryCompanyId, createdBy, partnerCommissionUsd, processingUsd, serviceMargins, transportUsd }) => {
  const fp = { appliedUsd, totalUsd, partnerCommissionUsd, processingUsd, serviceMargins, transportUsd, partnerRoles: (partnerRoles || []).map(r => ({ entityId: r.entityId, percent: r.percent })) };
  const tokenRef = buildPostingRef({ contractId, milestoneId: milestone?.id, eventType: 'Partial', amountFingerprint: fp });
  if (hasPostingToken(milestone, tokenRef.token)) return { skipped: true, token: tokenRef.token };
  const res = await postPart({ contractId, paymentLine: { id: milestone.id }, appliedUsd, totalUsd, partnerRoles, beneficiaryCompanyId, createdBy, partnerCommissionUsd, processingUsd, serviceMargins, transportUsd });
  return { ok: true, tokenRef, result: res };
};

export const postCancellationReversalsIdem = async ({ milestoneId, createdBy }) => {
  const res = await postRev({ paymentLineId: milestoneId, createdBy });
  return { ok: true, result: res };
};

export default {
  postInvoicedVendorCreditsIdem,
  postPaidCreditsIdem,
  postPartialCreditsIdem,
  postCancellationReversalsIdem,
  buildPostingRef,
  hasPostingToken,
};


